import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import ImageUploader from '@/components/feature/ImageUploader';
import Pagination from '@/components/base/Pagination';
import ConfirmModal from '@/components/base/ConfirmModal';

interface FAQ {
  id: number;
  question_zh: string;
  question_en: string;
  question_ru: string;
  answer_zh: string;
  answer_en: string;
  answer_ru: string;
  category: string;
  sort_order: number;
  status: string;
  cover_img: string;
}

const CATS = ['流程', '费用', '法律', '医疗', '其他'];
type LangTab = 'zh' | 'en' | 'ru';

function FAQModal({ faq, onClose, onSaved }: { faq: FAQ | null; onClose: () => void; onSaved: () => void }) {
  const isNew = !faq;
  const [lang, setLang] = useState<LangTab>('zh');
  const [form, setForm] = useState({
    question_zh: faq?.question_zh || '',
    question_en: faq?.question_en || '',
    question_ru: faq?.question_ru || '',
    answer_zh: faq?.answer_zh || '',
    answer_en: faq?.answer_en || '',
    answer_ru: faq?.answer_ru || '',
    category: faq?.category || '流程',
    sort_order: faq?.sort_order ?? 0,
    status: faq?.status || 'published',
    cover_img: faq?.cover_img || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSave = async () => {
    if (!form.question_zh || !form.answer_zh) { setErr('中文问题和答案不能为空'); return; }
    setSaving(true);
    setErr('');
    const payload = { ...form, updated_at: new Date().toISOString() };
    if (isNew) {
      const { error } = await supabase.from('faqs').insert(payload);
      if (error) { setErr(error.message); setSaving(false); return; }
    } else {
      const { error } = await supabase.from('faqs').update(payload).eq('id', faq!.id);
      if (error) { setErr(error.message); setSaving(false); return; }
    }
    setSaving(false);
    onSaved();
  };

  const langLabel: Record<LangTab, string> = { zh: '中文', en: 'English', ru: 'Русский' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-bold text-[#1a3a5c]">{isNew ? '添加FAQ' : '编辑FAQ'}</h2>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500"></i></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">分类</label>
              <select value={form.category} onChange={set('category')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e] cursor-pointer bg-white">
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">排序（数字越小越靠前）</label>
              <input type="number" value={form.sort_order} onChange={set('sort_order')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
            </div>
          </div>

          {/* Language tabs */}
          <div className="flex gap-2">
            {(['zh', 'en', 'ru'] as LangTab[]).map((l) => (
              <button key={l} onClick={() => setLang(l)} className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap ${lang === l ? 'bg-[#1a3a5c] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                {langLabel[l]}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">
              问题（{langLabel[lang]}）{lang === 'zh' && <span className="text-red-400">*</span>}
            </label>
            <input
              type="text"
              value={form[`question_${lang}` as keyof typeof form] as string}
              onChange={set(`question_${lang}`)}
              placeholder={`输入${langLabel[lang]}问题`}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">
              答案（{langLabel[lang]}）{lang === 'zh' && <span className="text-red-400">*</span>}
            </label>
            <textarea
              value={form[`answer_${lang}` as keyof typeof form] as string}
              onChange={set(`answer_${lang}`)}
              rows={5}
              placeholder={`输入${langLabel[lang]}答案`}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e] resize-none"
            />
          </div>

          {/* Cover image */}
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">配图（可选）</label>
            <ImageUploader
              currentUrl={form.cover_img}
              onChange={(url) => setForm((p) => ({ ...p, cover_img: url }))}
              placeholder="点击上传配图或粘贴 URL（JPG/PNG/WebP，最大2MB）"
              previewHeight="h-28"
              maxWidth={400}
            />
            <p className="text-xs text-gray-400 mt-1">用于 FAQ 详情页或列表展示时更直观</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">状态</label>
            <div className="flex gap-2">
              {['published', 'draft'].map((s) => (
                <button key={s} onClick={() => setForm((p) => ({ ...p, status: s }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap ${form.status === s ? 'bg-[#1a3a5c] text-white' : 'bg-gray-100 text-gray-500'}`}>
                  {s === 'published' ? '发布' : '草稿'}
                </button>
              ))}
            </div>
          </div>

          {err && <p className="text-red-500 text-xs">{err}</p>}

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-full text-sm cursor-pointer whitespace-nowrap">取消</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold py-3 rounded-full text-sm cursor-pointer whitespace-nowrap disabled:opacity-60">
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FAQsAdminPage() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [editing, setEditing] = useState<FAQ | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Batch
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; danger?: boolean; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });

  const fetchFaqs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase.from('faqs').select('*').order('sort_order').order('created_at');
      if (filterCat) query = query.eq('category', filterCat);
      const { data, error: supaErr } = await query;
      if (supaErr) throw supaErr;
      setFaqs(data ?? []);
      setSelectedIds([]);
      setCurrentPage(1);
    } catch (err: any) {
      setError(err?.message || '加载失败，请检查网络后重试');
    } finally {
      setLoading(false);
    }
  }, [filterCat]);

  useEffect(() => { fetchFaqs(); }, [fetchFaqs]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const totalPages = Math.max(1, Math.ceil(faqs.length / pageSize));
  const paginated = faqs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const allPageSelected = paginated.length > 0 && paginated.every((f) => selectedIds.includes(f.id));

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const pageIds = paginated.map((f) => f.id);
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      const merged = Array.from(new Set([...selectedIds, ...pageIds]));
      setSelectedIds(merged);
    }
  };

  const handleBatchUpdateStatus = async (newStatus: string) => {
    if (!selectedIds.length) return;
    const { error: supaErr } = await supabase.from('faqs').update({ status: newStatus }).in('id', selectedIds);
    if (supaErr) {
      setToast({ msg: '批量更新失败：' + supaErr.message, type: 'error' });
    } else {
      setToast({ msg: `成功更新 ${selectedIds.length} 条FAQ`, type: 'success' });
      setSelectedIds([]);
      fetchFaqs();
    }
  };

  const handleDelete = async (id: number) => {
    setConfirmModal({
      open: true,
      title: '删除FAQ',
      message: '确认删除该FAQ？',
      danger: true,
      onConfirm: async () => {
        await supabase.from('faqs').update({ status: 'draft' }).eq('id', id);
        setConfirmModal((p) => ({ ...p, open: false }));
        fetchFaqs();
      },
    });
  };

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]">FAQ管理</h1>
          <p className="text-gray-400 text-sm mt-1">配置首页FAQ折叠面板，支持多语言</p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowModal(true); }}
          className="flex items-center gap-1.5 bg-[#c9a96e] hover:bg-[#b8944f] text-white px-4 py-2 rounded-full text-sm cursor-pointer transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i>添加问答
        </button>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-2xl p-4 mb-5 flex flex-wrap gap-2 items-center">
        <button onClick={() => setFilterCat('')} className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap ${!filterCat ? 'bg-[#1a3a5c] text-white' : 'bg-gray-100 text-gray-500'}`}>全部</button>
        {CATS.map((c) => (
          <button key={c} onClick={() => setFilterCat(c)} className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap ${filterCat === c ? 'bg-[#1a3a5c] text-white' : 'bg-gray-100 text-gray-500'}`}>{c}</button>
        ))}
        <span className="ml-auto text-xs text-gray-400">{faqs.length} 条</span>
      </div>

      {/* Batch action bar */}
      {selectedIds.length > 0 && (
        <div className="bg-[#1a3a5c] rounded-2xl px-5 py-3 mb-4 flex items-center gap-3 flex-wrap">
          <span className="text-white text-sm font-medium">已选中 {selectedIds.length} 项</span>
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={() => handleBatchUpdateStatus('published')} className="px-3 py-1.5 rounded-full text-xs font-medium bg-green-50 text-green-600 hover:bg-green-100 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-check-line mr-1"></i>批量发布
            </button>
            <button onClick={() => handleBatchUpdateStatus('draft')} className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-draft-line mr-1"></i>批量下架
            </button>
            <button onClick={() => setSelectedIds([])} className="text-white/60 hover:text-white text-xs cursor-pointer ml-2">
              清除选择
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400"><i className="ri-loader-4-line text-2xl animate-spin block mb-2"></i>加载中...</div>
      ) : error ? (
        <div className="text-center py-12">
          <i className="ri-error-warning-line text-3xl text-red-300 mb-3 block"></i>
          <p className="text-red-500 text-sm mb-3">{error}</p>
          <button onClick={fetchFaqs} className="text-[#c9a96e] text-sm cursor-pointer">重试</button>
        </div>
      ) : (
        <div className="space-y-3">
          {paginated.map((f) => (
            <div key={f.id} className="bg-white rounded-2xl p-5">
              <div className="flex items-start gap-4">
                <div className="shrink-0 pt-0.5">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(f.id)}
                    onChange={() => toggleSelect(f.id)}
                    className="w-4 h-4 rounded border-gray-300 text-[#c9a96e] focus:ring-[#c9a96e] cursor-pointer"
                  />
                </div>
                <div className="shrink-0">
                  {f.cover_img && (
                    <div className="w-14 h-14 rounded-xl overflow-hidden mb-1.5">
                      <img src={f.cover_img} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <span className="inline-block bg-[#c9a96e]/10 text-[#c9a96e] text-xs px-2 py-0.5 rounded-full">{f.category}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1a3a5c] text-sm mb-1">{f.question_zh}</div>
                  <p className="text-gray-500 text-xs leading-relaxed line-clamp-2">{f.answer_zh}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {f.question_en && <span className="text-xs text-gray-300">EN ✓</span>}
                    {f.question_ru && <span className="text-xs text-gray-300">RU ✓</span>}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${f.status === 'published' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                      {f.status === 'published' ? '已发布' : '草稿'}
                    </span>
                    <span className="text-xs text-gray-300">排序: {f.sort_order}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => { setEditing(f); setShowModal(true); }} className="text-xs text-[#c9a96e] hover:underline cursor-pointer">编辑</button>
                  <button onClick={() => handleDelete(f.id)} className="text-xs text-gray-400 hover:text-red-500 cursor-pointer">删除</button>
                </div>
              </div>
            </div>
          ))}
          {faqs.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-questionnaire-line text-4xl mb-3 block"></i>暂无FAQ，点击上方按钮添加
            </div>
          )}
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={faqs.length}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
      />

      {showModal && (
        <FAQModal
          faq={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); fetchFaqs(); }}
        />
      )}

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        danger={confirmModal.danger}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((p) => ({ ...p, open: false }))}
      />

      {/* Toast */}
      {toast && (
        <div className="fixed top-6 right-6 z-[60] px-5 py-3 rounded-xl shadow-lg text-sm font-medium transition-all">
          <div className={`${toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'} px-4 py-3 rounded-xl flex items-center gap-2`}>
            <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-fill text-green-500' : 'ri-error-warning-fill text-red-500'}`}></i>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}